<template>
  <el-dialog
    id="create-modal"
    class="form-modal"
    :visible.sync="createVisible"
    v-loading="createProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="create-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">Create Call Queue</h1>
        <div class="modal-actions col-2">
          <el-button
            type="primary"
            :disabled="numUsed"
            @click="createOperation"
          >
            Create
          </el-button>
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
          >
            Cancel
          </el-button>
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          ></el-button>
        </div>
      </div>
    </div>

    <el-form>
      <el-tabs v-model="activeTabName">
        <el-tab-pane label="General" name="general">
          <el-checkbox v-model="createForm.recording"
            >Enable recording</el-checkbox
          >
          <div class="inline-form-group">
            <el-col :span="11">
              <el-input v-model="createForm.name" placeholder="Name">
                <template slot="prefix">Name</template>
              </el-input>
            </el-col>
            <el-col :span="1" />
            <el-col :span="12">
              <el-input v-model="createForm.number" placeholder="Number">
                <template slot="prefix">Number</template>
                <template slot="append" v-if="numUsed">
                  <in-use :inUse="numInUse" />
                </template>
              </el-input>
            </el-col>
          </div>
          <div class="inline-form-group">
            <el-col :span="11">
              <el-select
                v-model="createForm.ringStrategy"
                placeholder="Select ring strategy"
              >
                <el-option
                  v-for="item in ringStrategyOpts"
                  :key="item.label"
                  :label="item.label"
                  :value="item.value"
                />
                <template slot="prefix">Ring strategy</template>
              </el-select>
            </el-col>
            <el-col :span="1"></el-col>
            <el-col :span="12">
              <el-input
                v-model="createForm.timeout"
                type="number"
                min="1"
                placeholder="Enter timeout"
              >
                <template slot="prefix">Timeout (seconds)</template>
              </el-input>
            </el-col>
          </div>

          <el-select placeholder="Select branch" v-model="createForm.branchId" filterable>
            <template slot="prefix"> Branch </template>
            <el-option
              v-for="item in branchOptions"
              :key="item.id"
              :value="item.id"
              :label="item.name"
            />
          </el-select>

          <el-select
            placeholder="Select max queue length"
            v-model="createForm.maxlen"
          >
            <el-option
              v-for="(num, i) in 10"
              :key="i"
              :label="num * 5"
              :value="num * 5"
            />
            <template slot="prefix">Max queue length</template>
          </el-select>
          <el-tag class="mb-10">
            <i class="el-icon-info" style="margin-right: 4px"></i>
            The maximum number of callers waiting in queue
          </el-tag>

          <h2 class="form-section-header">Timeout destinations</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
                :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
            :selectedDestinations="createForm.timeoutDestinations"
            @destinationsSelected="onTimeoutDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />

          <h2 class="form-section-header">If queue is full</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
                :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
            :selectedDestinations="createForm.queueFullDestinations"
            @destinationsSelected="onQueueFullDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />

          <h2 class="form-section-header">Conditional routing</h2>
          <div class="mt-5">
            <el-checkbox v-model="createForm.nobodyHome">
              Enable redirecting call if no agents are available
              <el-popover placement="top" trigger="hover">
                <template slot="reference">
                  <i class="el-icon-question"></i>
                </template>
                <el-tag type="primary">
                  <span
                    >Followed when a call cannot be handled by any agents</span
                  >
                </el-tag>
              </el-popover>
            </el-checkbox>
            <div v-if="createForm.nobodyHome">
              <h4 class="form-section-header">If no agents are available</h4>
              <destination-selector
                v-if="createForm.nobodyHome"
                :isTerminalDestSelector="true"
                :draggable="true"
                :showHeader="false"
                :draggableDestTypes="draggableDestTypes"
                :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
                :selectedDestinations="createForm.noAvailableAgentsDestinations"
                @destinationsSelected="noAvailableAgentsDestinationsSelection"
                title=""
                :clearFields="clearFormFields"
              />
            </div>

            <el-checkbox v-model="createForm.noFreeMember">
              Enable redirecting call if all agents busy
              <el-popover placement="top" trigger="hover">
                <template slot="reference">
                  <i class="el-icon-question"></i>
                </template>
                <el-tag type="primary">
                  <span
                    >Followed when agent can't immediately service a call</span
                  >
                </el-tag>
              </el-popover>
            </el-checkbox>
            <div v-if="createForm.noFreeMember">
              <h4 class="form-section-header">If all agents are busy</h4>
              <destination-selector
                :isTerminalDestSelector="true"
                :draggable="true"
                :showHeader="false"
                :draggableDestTypes="draggableDestTypes"
                :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
                :selectedDestinations="createForm.allAgentsBusyDestinations"
                @destinationsSelected="allAgentsBusyDestinationsSelection"
                title=""
                :clearFields="clearFormFields"
              />
            </div>
          </div>

          <h2 class="form-section-header">Abandoned call notifications</h2>
          <div class="mt-5">
            <el-checkbox v-model="createForm.notifyAbandoned"
              >Enable abandoned call notifications</el-checkbox
            >
            <el-input
              v-if="createForm.notifyAbandoned"
              v-model="createForm.notifyAbandonedEmail"
              placeholder="Notify abandon email"
            >
              <template slot="prefix">Notify abandon email</template>
            </el-input>
          </div>
        </el-tab-pane>

        <el-tab-pane label="Exit and Callback Settings" name="exitAndCallback">
          <el-checkbox v-model="createForm.callback">
            Enable queue callback
          </el-checkbox>

          <div v-if="createForm.callback">
            <h3 class="form-section-header">Queue callback settings</h3>
            <div class="inline-form-group">
              <el-col :span="11">
                <dads-select
                  placeholder="Caller id number"
                  v-model="createForm.callbackCidNum"
                  filterable
                  clearable
                  remote
                  :remote-method="remoteSearchForCidNums"
                >
                  <el-option
                    v-for="(item, i) in cidNumOptions"
                    :key="i"
                    :label="item.label | formatNumber"
                    :value="item.label"
                  />
                  <template slot="prefix">Caller id number</template>
                </dads-select>
              </el-col>
              <el-col :span="1" />
              <el-col :span="12">
                <el-select
                  placeholder="Select max attempts"
                  v-model="createForm.callbackMaxAttempts"
                >
                  <el-option
                    v-for="(n, i) in 5"
                    :key="i"
                    :label="n"
                    :value="n"
                  />
                  <template slot="prefix">Max attempts</template>
                </el-select>
              </el-col>
            </div>
            <existing-mediafile
              title="Play when connecting To agent"
              :multiple="false"
              :clearState="clearFormFields"
              :newMediafile="newMediaFile"
              @mediafileSelected="createForm.agentConnectMeId = $event"
            />
            <new-mediafile @mediafileSaved="newMediaFile = $event" />
          </div>

          <el-checkbox v-model="queueExitKey">
            Enable queue exit key
          </el-checkbox>

          <div v-if="queueExitKey">
            <h3 class="form-section-header">Exit key settings</h3>
            <el-select
              v-model="createForm.exitKey"
              placeholder="Select exit key"
              v-if="queueExitKey"
              clearable
            >
              <el-option
                v-for="(item, i) in exitKeyOptions"
                :key="i"
                :label="item"
                :value="item"
              />
              <template slot="prefix">Exit key</template>
            </el-select>

            <dads-select
              v-if="createForm.exitKey === 'Use virtual receptionist options'"
              placeholder="Select virtual receptionist"
              v-model="createForm.ivrId"
              clearable
              filterable
            >
              <el-option
                v-for="(item, i) in ivrOptions"
                :key="i"
                :label="item.name"
                :value="item.id"
              />

              <template slot="prefix">Virtual receptionist</template>
            </dads-select>

            <div v-else-if="createForm.exitKey !== ''">
              <h2 class="form-section-header">Exit Key Destinations</h2>
              <destination-selector
                :isTerminalDestSelector="true"
                :draggable="true"
                :showHeader="false"
                :draggableDestTypes="draggableDestTypes"
                :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
                :selectedDestinations="createForm.exitKeyDestinations"
                @destinationsSelected="onExitKeyDestinationsSelection"
                title=""
                :clearFields="clearFormFields"
              />
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="Media" name="media">
          <new-mediafile @mediafileSaved="newMediaFile = $event" />
          <el-select
            v-model="playToCaller"
            placeholder="Play to the caller while waiting in the queue"
          >
            <el-option
              v-for="(item, i) in playToCallerOpts"
              :key="i"
              :label="item.label"
              :value="item.value"
            />
            <template slot="prefix"
              >Play to the caller while waiting in the queue</template
            >
          </el-select>

          <h2 class="form-section-header">Periodic announcements</h2>
          <el-checkbox v-model="enablePeriodicAnnounce">
            Enable periodic announcements
          </el-checkbox>
          <el-select
            v-model="createForm.periodicAnnounceFrequency"
            v-if="enablePeriodicAnnounce"
            placeholder="Select frequency"
          >
            <div v-for="(n, i) in 120" :key="i">
              <el-option v-if="n !== 1" :label="n * 5" :value="n * 5" />
            </div>
            <template slot="prefix">Frequency</template>
          </el-select>
          <existing-mediafile
            v-show="enablePeriodicAnnounce"
            title="Periodic Announce Message"
            :multiple="false"
            :clearState="clearFormFields"
            :newMediafile="newMediaFile"
            :defaultMessage="true"
            @mediafileSelected="createForm.periodicAnnounceMeId = $event"
          />

          <h2 class="form-section-header">Hold time announcements</h2>
          <div class="mt-5">
            <el-checkbox v-model="createForm.reportHoldTime">
              Enable reporting caller's hold time to agent
            </el-checkbox>
            <div class="inline-form-group">
              <el-col :span="10">
                <el-select
                  v-model="createForm.announceHoldTime"
                  placeholder="Select announce hold time"
                >
                  <el-option
                    v-for="(item, i) in announceHoldTimeOptions"
                    :key="i"
                    :label="item.label"
                    :value="item.value"
                  />
                  <template slot="prefix">Announce hold time</template>
                </el-select>
              </el-col>
            </div>
            <div v-if="createForm.announceHoldTime !== 'no'">
              <existing-mediafile
                title="Est hold time message"
                :multiple="false"
                :clearState="clearFormFields"
                :newMediafile="newMediaFile"
                :defaultMessage="true"
                @mediafileSelected="createForm.holdtimeMeId = $event"
              />
            </div>
          </div>

          <h2 class="form-section-header">Position announcements</h2>
          <div class="inline-form-group">
            <el-col :span="10">
              <el-select
                v-model="createForm.announcePosition"
                placeholder="Announce position"
              >
                <el-option
                  v-for="(item, i) in announcePositionOptions"
                  :key="i"
                  :label="item.label"
                  :value="item.value"
                />
                <template slot="prefix">Announce position</template>
              </el-select>
            </el-col>
            <el-col :span="1" />
            <el-col :span="13">
              <el-input
                v-if="
                  createForm.announcePosition === 'more' ||
                  createForm.announcePosition === 'limit'
                "
                v-model="createForm.announcePositionLimit"
                placeholder="Enter limit"
                min="1"
              >
                <template slot="prefix">Limit</template>
              </el-input>
            </el-col>
          </div>

          <div v-if="createForm.announcePosition !== 'no'">
            <existing-mediafile
              title="You are the next message"
              :multiple="false"
              :clearState="clearFormFields"
              :newMediafile="newMediaFile"
              :defaultMessage="true"
              @mediafileSelected="createForm.youAreNextMeId = $event"
            />

            <existing-mediafile
              title="There are message"
              :multiple="false"
              :clearState="clearFormFields"
              :newMediafile="newMediaFile"
              :defaultMessage="true"
              @mediafileSelected="createForm.thereAreMeId = $event"
            />

            <existing-mediafile
              title="Calls waiting message"
              :multiple="false"
              :clearState="clearFormFields"
              :newMediafile="newMediaFile"
              :defaultMessage="true"
              @mediafileSelected="createForm.callsWaitingMeId = $event"
            />

            <existing-mediafile
              title="Thank you message"
              :multiple="false"
              :clearState="clearFormFields"
              :newMediafile="newMediaFile"
              :defaultMessage="true"
              @mediafileSelected="createForm.thankYouMeId = $event"
            />
          </div>

          <div
            v-if="
              createForm.announceHoldTime !== 'no' ||
              createForm.announcePosition !== 'no'
            "
          >
            <h2 class="form-section-header">
              Hold and position announcement frequency
            </h2>
            <el-col :span="24">
              <el-select
                v-model="createForm.announceFrequency"
                placeholder="Announce frequency"
              >
                <div v-for="(n, i) in 12" :key="i">
                  <el-option v-if="n !== 1" :label="n * 10" :value="n * 10" />
                </div>
                <template slot="prefix">Announce frequency</template>
              </el-select>
            </el-col>
          </div>
        </el-tab-pane>

        <el-tab-pane label="Agent Settings" name="agent">
          <el-checkbox v-model="createForm.ringInUse">
            Enable ringing agent with in use status
          </el-checkbox>
          <el-checkbox v-model="createForm.autoPause">
            Enable auto pause
          </el-checkbox>
          <el-checkbox v-model="createForm.autologout">
            Enable auto logout
          </el-checkbox>
          <el-checkbox v-model="ringAgentsOnlyOnce">
            Enable ringing agents only once
          </el-checkbox>
          <el-checkbox v-model="createForm.autofill">
            Enable auto fill
          </el-checkbox>
          <el-checkbox v-model="createForm.agentPickup">
            Enable agent call pickup
          </el-checkbox>
          <div class="inline-form-group">
            <el-col :span="10">
              <div class="flex align-center">
                <el-checkbox v-model="tierTimeoutEnabled">
                  Enable tier timeout rule
                </el-checkbox>
                <el-popover placement="top" trigger="hover">
                  <template slot="reference">
                    <i class="el-icon-question ml-5"></i>
                  </template>
                  <span class="black-text max-w-200">
                    For ring all queues agents are called in combined tier groups per timeout
                    <br>
                    Example: Tier 1 rings -> Timeout -> Tier 1 and 2 rings -> Timeout -> Tier 1, 2, and 3 rings.
                    <br>
                    This setting does not override agent ring duration - it is recommended to keep ring duration at or below the tier threshold
                  </span>
                </el-popover>
              </div>
            </el-col>
            <el-col :span="1"></el-col>
            <el-col :span="13" v-if="tierTimeoutEnabled">
              <el-select
                v-model="createForm.tierTimeout"
                placeholder="Select timeout"
              >
                <el-option
                  label="5 seconds"
                  value="5"
                />
                <el-option
                  label="10 seconds"
                  value="10"
                />
                <el-option
                  label="15 seconds"
                  value="15"
                />
                <el-option
                  label="30 seconds"
                  value="30"
                />
                <el-option
                  label="60 seconds"
                  value="60"
                />
                <template slot="prefix">Timeout (s)</template>
              </el-select>
            </el-col>
          </div>


          <div class="inline-form-group">
            <el-col :span="10">
              <el-select
                v-model="createForm.agentTimeout"
                placeholder="Agent timeout"
              >
                <el-option
                  v-for="(n, i) in 19"
                  :key="i"
                  :label="n + 1"
                  :value="(n + 1) * 5"
                />
                <template slot="prefix">Agent timeout (rings)</template>
              </el-select>
            </el-col>
            <el-col :span="1"></el-col>
            <el-col :span="13">
              <el-input
                v-model="createForm.agentWrapupTime"
                min="0"
                type="number"
                placeholder="Agent wrap up time"
              >
                <template slot="prefix">Agent wrap up time</template>
              </el-input>
            </el-col>
          </div>

          <h2 class="form-section-header">Assign allowed agents</h2>
          <draggable
            :key="draggableKey"
            @change="changeDraggableKey"
            v-model="createForm.allowedAgents"
          >
            <transition-group name="list" tag="div">
              <div
                class="inline-form-group"
                v-for="(item, i) in createForm.allowedAgents"
                :key="i + '$(^^)$'"
              >
                <el-col :span="15">
                  <dads-select
                    :placeholder="`Select Agent ${i + 1}`"
                    :remote-method="(query) => remoteSearchExtensions(query, i)"
                    v-model="item.id"
                    remote
                    filterable
                    clearable
                    @change="($event) => onAllowedAgentSelection($event, i)"
                  >
                    <el-option
                      v-for="(el, j) in item.options"
                      :key="j"
                      :label="el.name + ' - ' + el.number"
                      :value="el.id"
                    />
                    <template slot="prefix">Agent</template>
                  </dads-select>
                </el-col>
                <el-col :span="1" />
                <el-col :span="3">
                  <el-select
                    v-if="tierTimeoutEnabled"
                    class="ml-5"
                    placeholder="Tier"
                    v-model="item.penalty"
                    @change="($event) => onAgentTierSelect($event, item.id, i)"
                  >
                    <el-option
                      label="1"
                      value="1"
                    />
                    <el-option
                      label="2"
                      value="2"
                    />
                    <el-option
                      label="3"
                      value="3"
                    />
                    <el-option
                      label="4"
                      value="4"
                    />
                    <el-option
                      label="5"
                      value="5"
                    />
                    <template slot="prefix">Tier</template>
                  </el-select>
                </el-col>

                <el-col :span="5" class="text-right">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="`Remove Agent ${i + 1}`"
                    placement="top"
                  >
                    <el-button
                      class="ml-5"
                      style="float: right; margin-right: 1px"
                      @click="removeAllowedAgent(i)"
                      :disabled="i === 0"
                      icon="el-icon-delete"
                    />
                  </el-tooltip>
                  <el-button icon="el-icon-rank" type="default" />
                </el-col>
              </div>
            </transition-group>
          </draggable>

          <el-button
            type="text"
            icon="el-icon-circle-plus-outline"
            :disabled="disabledToAddAgent"
            @click="addAllowedAgent"
          >
            Add Agent
          </el-button>
        </el-tab-pane>
      </el-tabs>
    </el-form>
  </el-dialog>
</template>

<script>
import Draggable from "vuedraggable";
import DestinationSelector from "@/components/DestinationSelector.vue";
import ExistingMediafile from "@/components/ExistingMediafile";
import NewMediafile from "@/components/NewMediafile";
import feathersClient from "@/feathers/feathers-client";
import DadsSelect from "@/components/select";
import InUse from "@/components/InUse";

export default {
  name: "CreateCallQueuesForm",

  props: {
    createVisible: { required: true },
    clearFormFields: { required: true },
    createProcessing: { required: true },
    ivrOptions: { required: true },
    cidNumOptions: { required: true },
    numInUse: { required: true },
    draggableDestTypes: { required: true },
    branchOptions: { required: true },
  },

  components: {
    DestinationSelector,
    ExistingMediafile,
    NewMediafile,
    Draggable,
    DadsSelect,
    InUse,
  },

  data() {
    return {
      newMediaFile: null,
      periodicAnnounceNewMediafile: null,
      holdTimeNewMediafile: null,
      defaultMsgNewMediafile: null,
      agentConnectNewMediafile: null,
      draggableKey: "",
      activeTabName: "general",
      queueExitKey: false,
      destsModalVisible: false,
      exitKeyOptions: [
        "0",
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "Use virtual receptionist options",
      ],
      ringStrategyOpts: [
        { label: "Fewest calls", value: "FEWESTCALLS" },
        // { label: "Least recent", value: "LEASTRECENT" },
        { label: "Linear", value: "LINEAR" },
        { label: "Random", value: "RANDOM" },
        { label: "Ring all", value: "RINGALL" },
        { label: "Round robin", value: "RRMEMORY" },
      ],
      playToCallerOpts: [
        { label: "Music on hold", value: "" },
        { label: "Ringing", value: "r" },
        { label: "Music on hold, then ringing", value: "R" },
      ],
      playToCaller: null,
      ringAgentsOnlyOnce: false,
      announceHoldTimeOptions: [
        { label: "No", value: "no" },
        { label: "Yes", value: "yes" },
        { label: "Once", value: "once" },
      ],
      announcePositionOptions: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
        { label: "If less than", value: "limit" },
        { label: "No more than", value: "more" },
      ],
      enablePeriodicAnnounce: false,
      tierTimeoutEnabled: false,

      createForm: {
        tenantId: null,
        tenantCode: "",

        // ⭐️ General Tab ⭐️ //
        name: "",
        number: "",
        recording: false,
        ringStrategy: "",
        timeout: 120,
        timeoutDestinations: [],
        maxlen: "",
        queueFullDestinations: [],
        nobodyHome: false,
        noAvailableAgentsDestinations: [],
        noFreeMember: false,
        allAgentsBusyDestinations: [],
        notifyAbandoned: false,
        notifyAbandonedEmail: "",
        dids: [],
        branchId: null,

        // ⭐️ Exit and Callback Settings ⭐️ //
        callback: false,
        callbackCidNum: "",
        callbackMaxAttempts: 1,
        agentConnectMeId: null,
        exitKey: "",
        exitKeyDestinations: [],
        ivrId: null,

        // ⭐️ Media ⭐️ //
        parameters: "",
        reportHoldTime: false,
        periodicAnnounceFrequency: 10,
        periodicAnnounceMeId: null,
        announceHoldTime: "no",
        holdtimeMeId: null,
        announcePosition: "no",
        announcePositionLimit: null,
        youAreNextMeId: null,
        thereAreMeId: null,
        callsWaitingMeId: null,
        thankYouMeId: null,
        announceFrequency: 0,

        // ⭐️ Agent Settings ⭐️ //
        ringInUse: false,
        autoPause: false,
        autologout: false,
        agentTimeout: 30,
        agentWrapupTime: 0,
        autofill: "no",
        tierTimeout: null,
        allowedAgents: [
          {
            id: null,
            options: [],
            value: null,
            penalty: 1,
          },
        ],
        enableAgentDestinations: false,
        agentPickup: false,
      },
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    number() {
      return this.createForm.number;
    },

    disabledToAddAgent() {
      if (this.createForm.allowedAgents.length) {
        const lastIdx = this.createForm.allowedAgents.length - 1;
        return !this.createForm.allowedAgents[lastIdx].id;
      }
    },

    announceHoldTime() {
      return this.createForm.announceHoldTime;
    },

    numUsed() {
      return this.numInUse && this.numInUse.inUse;
    },
  },

  watch: {
    clearFormFields(value) {
      if (value) {
        this.clearFields();
        this.getDefaultBranch();
      }
    },

    number(val) {
      if (val) {
        this.$emit("checkAvailableNum", val);
      }
    },

    announceHoldTime(val) {
      if (val === "no") {
        this.createForm.holdtimeMeId = 0;
        this.holdTimeNewMediafile = null;
      }
    },
  },

  methods: {
    async getDefaultBranch() {
      try {
        const [defaultBranch] = await feathersClient.service("branches").find({
          query: {
            tenantId: this.selectedTenant.id,
            default: 1
          }
        });
        this.createForm.branchId = defaultBranch.id;
      } catch (error) {
        console.log(error)
      }
    },
    changeDraggableKey() {
      const randomString = () => Math.random().toString(36).slice(2);
      this.draggableKey = `${randomString()}-${randomString()}$${randomString()}`;
    },

    onAllowedAgentSelection(agentId, i) {
      if (!agentId) {
        this.createForm.allowedAgents[i].value = null;
        return;
      }

      const agent = this.createForm.allowedAgents[i].options.find(
        (ag) => ag.id === agentId
      );

      this.createForm.allowedAgents[i].value = {
        id: agentId,
        name: agent.name,
        number: agent.number,
        penalty: this.tierTimeoutEnabled ? 1 : 0
      };
    },

    onAgentTierSelect(value, agentId, i) {
      if (!agentId) {
        this.createForm.allowedAgents[i].value = null;
        return;
      }

      const agent = this.createForm.allowedAgents[i].options.find(
        (ag) => ag.id === agentId
      );

      this.createForm.allowedAgents[i].value = {
        id: agentId,
        name: agent.name,
        number: agent.number,
        penalty: this.tierTimeoutEnabled ? value : 0,
      };
    },

    onTimeoutDestinationsSelection(destinations) {
      this.createForm.timeoutDestinations = [...destinations];
    },

    onQueueFullDestinationsSelection(destinations) {
      this.createForm.queueFullDestinations = [...destinations];
    },

    noAvailableAgentsDestinationsSelection(destinations) {
      this.createForm.noAvailableAgentsDestinations = [...destinations];
    },

    allAgentsBusyDestinationsSelection(destinations) {
      this.createForm.allAgentsBusyDestinations = [...destinations];
    },

    onExitKeyDestinationsSelection(destinations) {
      this.createForm.exitKeyDestinations = [...destinations];
    },

    clearFields() {
      this.defaultMsgNewMediafile = null;
      this.onAgentConnectNewMediafile = null;
      this.holdTimeNewMediafile = null;
      this.periodicAnnounceNewMediafile = null;
      this.draggableKey = "";
      this.activeTabName = "general";
      this.playToCaller = null;
      this.ringAgentsOnlyOnce = false;
      this.queueExitKey = false;
      this.enablePeriodicAnnounce = false;
      this.tierTimeoutEnabled = false;
      Object.keys(this.createForm).forEach((key) => {
        if (typeof (this.createForm[key] === "boolean")) {
          this.createForm[key] = false;
        }

        if (typeof (this.createForm[key] === "string")) {
          this.createForm[key] = "";
        }

        if (Array.isArray(this.createForm[key])) {
          this.createForm[key] = [];
        }
      });

      this.createForm.dids = [];

      this.createForm.tenantId = null;
      this.createForm.timeout = 120;

      this.createForm.callbackMaxAttempts = 1;
      this.createForm.agentConnectMeId = null;

      this.createForm.ivrId = null;
      this.createForm.periodicAnnounceFrequency = 10;
      this.createForm.periodicAnnounceMeId = null;
      this.createForm.announceHoldTime = "no";
      this.createForm.holdtimeMeId = null;
      this.createForm.announcePosition = "no";
      this.createForm.announcePositionLimit = null;
      this.createForm.youAreNextMeId = null;
      this.createForm.thereAreMeId = null;
      this.createForm.callsWaitingMeId = null;
      this.createForm.thankYouMeId = null;
      this.createForm.announceFrequency = 0;
      this.createForm.agentTimeout = 30;
      this.createForm.agentWrapupTime = 0;
      this.createForm.autofill = "no";
      this.createForm.allowedAgents = [
        {
          id: null,
          options: [],
          value: null,
          penalty: 1,
        },
      ];
      this.createForm.enableAgentDestinations = false;
    },

    validateDestinations(destinations, type) {
      if (destinations.length > 1 && destinations.find((dst) => !dst.id)) {
        this.$notify({
          title: `Empty ${type} Destination field`,
          message: "Do not leave any destination field empty.",
          type: "error",
        });
        return;
      }

      let finalDestinations = [];
      if (destinations.length === 1 && destinations[0].id === null) {
        return finalDestinations;
      }

      if (destinations.length) {
        destinations.forEach((dst, idx) => (dst.order = idx + 1));

        finalDestinations = destinations.map(({ id, type, order }) => ({
          id,
          type,
          order,
        }));
      }
      return finalDestinations;
    },

    createOperation() {
      const createBody = { ...this.createForm };
      createBody.recording = createBody.recording ? "yes" : "no";
      createBody.nobodyHome = createBody.nobodyHome ? "on" : "";
      createBody.noFreeMember = createBody.noFreeMember ? "on" : "";
      createBody.notifyAbandoned = createBody.notifyAbandoned ? "on" : "";
      createBody.callback = createBody.callback ? "on" : "";
      createBody.reportHoldTime = createBody.reportHoldTime ? "yes" : "no";
      createBody.ringInUse = createBody.ringInUse ? "yes" : "no";
      createBody.autoPause = createBody.autoPause ? "yes" : "no";
      createBody.autologout = createBody.autologout ? 1 : 0;
      createBody.agentPickup = createBody.enableAgentPickup ? 1 : 0;
      createBody.branchId ? createBody.branchId : null;
      createBody.autofill = createBody.autofill ? "yes" : "no";
      createBody.periodicAnnounceFrequency = this.enablePeriodicAnnounce ? createBody.periodicAnnounceFrequency : 0;
      createBody.tierTimeout = this.tierTimeoutEnabled ? createBody.tierTimeout : null;

      createBody.tenantId = this.selectedTenant.id;
      createBody.tenantCode = this.selectedTenant.tenantCode;
      createBody.announceFrequency = createBody.announceHoldTime === 'no' && createBody.announcePosition === 'no' ? 0 : createBody.announceFrequency

      // ⭐️ Route Call If No Agents Logged In (qu_nobodyhome) ⭐️ //
      if (createBody.nobodyHome === "") {
        createBody.noAvailableAgentsDestinations = [];
      }

      // ⭐️ Route Call If All Agents Busy (qu_nofreemembers) ⭐️ //
      if (createBody.noFreeMember === "") {
        createBody.allAgentsBusyDestinations = [];
      }

      // ⭐️ Notify Abandon Email (qu_notifyabandonedemail) ⭐️ //
      if (createBody.notifyAbandoned === "") {
        createBody.notifyAbandonedEmail = "";
      }

      // ⭐️ Call Back Caller Id Number (qu_callbackcalleridnum) ⭐️ //
      if (createBody.callback === "") {
        createBody.callbackCidNum = "";
      }

      // ⭐️ announce_position_limit ⭐️ //
      if (
        createBody.announcePosition === "yes" ||
        createBody.announcePosition === "no"
      ) {
        createBody.announcePositionLimit = 0;
      }

      if (createBody.ivrId === null) {
        createBody.ivrId = 0;
      }

      if (createBody.announcePositionLimit === null) {
        createBody.announcePositionLimit = 0;
      }

      // ⭐️ Handling qu_parameters ⭐️ //
      if (this.playToCaller === null) {
        this.$notify({
          title: "Invalid field",
          message: "Play to the caller is required!",
          type: "error",
        });
        return;
      } else {
        createBody.parameters = `${this.ringAgentsOnlyOnce ? "n" : ""}t${
          this.playToCaller
        }`;
      }

      if (createBody.announcePosition === "no") {
        createBody.youAreNextMeId = 0;
        createBody.thereAreMeId = 0;
        createBody.callsWaitingMeId = 0;
        createBody.thankYouMeId = 0;
      }

      createBody.periodicAnnounceMeId = createBody.periodicAnnounceMeId
        ? createBody.periodicAnnounceMeId
        : 0;
      createBody.holdtimeMeId = createBody.holdtimeMeId
        ? createBody.holdtimeMeId
        : 0;
      createBody.youAreNextMeId = createBody.youAreNextMeId
        ? createBody.youAreNextMeId
        : 0;
      createBody.thereAreMeId = createBody.thereAreMeId
        ? createBody.thereAreMeId
        : 0;
      createBody.callsWaitingMeId = createBody.callsWaitingMeId
        ? createBody.callsWaitingMeId
        : 0;
      createBody.thankYouMeId = createBody.thankYouMeId
        ? createBody.thankYouMeId
        : 0;

      // ⭐️ Allowed Agents ⭐️ //
      const allowedAgents = new Set();
      createBody.allowedAgents.forEach((ag) => {
        if (ag.id) {
          allowedAgents.add(ag.id);
        }
      });

      createBody.allowedAgents = [];
      allowedAgents.forEach((agId) => {
        const matchedAgent = this.createForm.allowedAgents.find(
          (agent) => agId === agent.id
        );
        matchedAgent.value.penalty = this.tierTimeoutEnabled ? matchedAgent.value.penalty : 0;
        if (matchedAgent) {
          createBody.allowedAgents.push(matchedAgent.value);
        }
      });

      const timeoutDestinations = this.validateDestinations(
        createBody.timeoutDestinations,
        "TIMEOUT"
      );
      const queueFullDestinations = this.validateDestinations(
        createBody.queueFullDestinations,
        "QUEUE-FULL"
      );

      const noAvailableAgentsDestinations = this.validateDestinations(
        createBody.noAvailableAgentsDestinations,
        "NO-AVAILABLE-AGENT"
      );

      const allAgentsBusyDestinations = this.validateDestinations(
        createBody.allAgentsBusyDestinations,
        "ALL-AGENTS-BUSY"
      );

      const exitKeyDestinations = this.validateDestinations(
        createBody.exitKeyDestinations,
        "EXITKEY"
      );

      if (
        timeoutDestinations &&
        queueFullDestinations &&
        noAvailableAgentsDestinations &&
        allAgentsBusyDestinations &&
        exitKeyDestinations
      ) {
        createBody.timeoutDestinations = timeoutDestinations;
        createBody.queueFullDestinations = queueFullDestinations;
        createBody.noAvailableAgentsDestinations =
          noAvailableAgentsDestinations;
        createBody.allAgentsBusyDestinations = allAgentsBusyDestinations;
        createBody.exitKeyDestinations = exitKeyDestinations;
      } else {
        return;
      }

      this.$emit("createOperation", createBody);
    },

    cancelOperation() {
      this.$emit("cancelOperation", "create");
    },

    remoteSearchForCidNums(query) {
      this.$emit("remoteSearchForCidNums", query);
    },

    async searchExtensions(query) {
      const searchTerm = (query + "")
        .replace(/[\\"']/g, "\\$&")
        .replace(/\u0000/g, "\\0");

      let queryTerm = {
        tenantId: this.selectedTenant.id,
        $limit: 25,
        $or: [
          { number: { $like: `%${searchTerm}%` } },
          { name: { $like: `%${searchTerm}%` } },
        ],
      };

      return await feathersClient
        .service("extensions")
        .find({ query: queryTerm });
    },

    async remoteSearchExtensions(query, idx) {
      if (query && query.length) {
        const exts = await this.searchExtensions(query);
        this.createForm.allowedAgents[idx].options = [...exts];
      }
    },

    addAllowedAgent() {
      this.createForm.allowedAgents.push({
        options: [],
        id: null,
        value: null,
      });
    },

    removeAllowedAgent(idx) {
      this.createForm.allowedAgents.splice(idx, 1);
    },
  },

  filters: {
    formatNumber(value) {
      return value
        ? value.replace(/(\d{3})(\d{3})(\d{4})/g, "($1)-$2-$3")
        : value;
    },
  },

  async created() {
    this.changeDraggableKey();
    await this.getDefaultBranch();
  },
};
</script>

<style lang="scss" scoped>
.el-tabs {
  max-width: 600px;
  margin: auto;
}

.el-checkbox {
  margin-left: 0;
}
</style>
